import { useTranslation } from 'react-i18next'
import Link from 'components/link/Link.tsx'
import { routes } from 'types/routes.ts'
import { Navigate } from 'react-router'
import EmptyState from 'components/return-later/EmptyState.tsx'
import { useStoreSelector } from 'hooks/store.ts'
import dayjs from 'dayjs'
import { selectCurrentServiceContracts } from 'store/contact/slice.ts'
import { trackEvent } from 'utils/tracking.ts'
import { CONTRACT_STATUSES, ContractStatus } from '@boltenergy-be/shared'
import InstalmentCard from 'components/instalment-card/InstalmentCard.tsx'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { isActiveContract } from 'utils/contracts.ts'

const Instalment = () => {
  // REDUX STORE
  const serviceContracts = useStoreSelector(selectCurrentServiceContracts)
  const { electricity, gas } = serviceContracts
  const hasActiveGasContract = isActiveContract(gas)

  // Access rights
  const instalmentAccess = useGetAccessRight(ACCESS_RIGHT_KEYS.instalment)
  const billingCycleAccess = useGetAccessRight(ACCESS_RIGHT_KEYS.billingCycles)

  // i18n
  const { t } = useTranslation(['billing', 'common'])

  // constants
  const showNoDataState =
    !instalmentAccess.showContent &&
    ((electricity.detail.status === CONTRACT_STATUSES.terminated && dayjs().isSameOrAfter(electricity.detail.effectiveEndDate)) ||
      ([CONTRACT_STATUSES.cancelled, CONTRACT_STATUSES.rejected] as ContractStatus[]).includes(electricity.detail.status))

  return !instalmentAccess.canAccess ? (
    <Navigate to={routes.BILLING_INVOICES} replace />
  ) : !instalmentAccess.showContent ? (
    <EmptyState
      icon={showNoDataState ? 'cross' : undefined}
      title={
        showNoDataState
          ? t(($) => $.emptyState.noData.title, {
              dataTranslation: t(($) => $.tabs.instalment).toLowerCase(),
              ns: 'common'
            })
          : undefined
      }
      description={
        showNoDataState
          ? t(($) => $.emptyState.noData.description, {
              ns: 'common'
            })
          : t(($) => $.editInstalment.returnLater)
      }
    />
  ) : (
    <>
      <InstalmentCard
        totalInstalment={(electricity?.detail?.instalment || 0) + (hasActiveGasContract ? gas?.detail?.instalment || 0 : 0)}
        electricityInstalment={electricity?.detail?.instalment}
        gasInstalment={hasActiveGasContract ? gas?.detail?.instalment : undefined}
        className="mb-500"
      />

      {billingCycleAccess.canAccess.current && (
        <p className="mb-400">
          {t(($) => $.instalmentOverview.explainer, {
            context: billingCycleAccess.showContent.current ? 'withEstimation' : 'noEstimation'
          })}
        </p>
      )}

      {billingCycleAccess.showContent.current && (
        <Link
          href={routes.BILLING_INSTALMENT_CHECK}
          representation="button"
          isFullwidth
          onClick={() =>
            trackEvent(
              {
                category: 'billing',
                object: 'instalment_check',
                action: 'start'
              },
              { from: routes.BILLING_INSTALMENT }
            )
          }
        >
          {t(($) => $.instalmentOverview.action)}
        </Link>
      )}

      <Link
        href={routes.BILLING_EDIT_INSTALMENT}
        className="mt-400"
        representation="button"
        variant="secondary"
        isFullwidth
        onClick={() =>
          trackEvent(
            {
              category: 'billing',
              object: 'edit_instalment',
              action: 'start'
            },
            { from: routes.BILLING_INSTALMENT }
          )
        }
      >
        {t(($) => $.instalment.editInstalment)}
      </Link>
    </>
  )
}

export default Instalment
